import { HARPOON_RELOAD_DURATION_MS, HARPOON_SCAN_POWER_MW } from '../../shared/constants.js';
import { t } from '../i18n.js';
import type { ControlInput, GameSnapshot, RoomState, SubmarineState } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import { HarpoonCameraView } from '../rendering/HarpoonCameraView.js';
import { clamp, createCard } from '../controllerUiHelpers.js';
import { DEFAULT_GRAPHICS_QUALITY, type GraphicsQuality } from '../rendering/quality.js';
import { DEFAULT_RENDER_TUNING, type RenderTuning } from '../rendering/renderTuning.js';

export interface HarpoonRoomController {
  render: () => void;
  tryAction: () => boolean;
  tryScan: () => boolean;
  bindUiElements: (
    actionButton: HTMLButtonElement | null,
    scanButton: HTMLButtonElement | null,
    reloadProgressBar: HTMLDivElement | null,
    reloadStatusLabel: HTMLDivElement | null
  ) => void;
  clearUiElements: () => void;
  setRenderTuning: (tuning: RenderTuning) => void;
  setSnapshot: (snapshot: GameSnapshot) => void;
  setRoomState: (state: RoomState) => void;
  dispose: () => void;
}

export interface HarpoonRoomControllerDeps {
  controls: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
  getRenderTuning: () => RenderTuning;
  getSubmarinePower: (submarine: SubmarineState) => number;
  getGraphicsQuality: () => GraphicsQuality;
  getFpsOverride?: () => number | undefined;
}

const noopEmitControl = (_input: ControlInput): void => {};
const defaultGetRenderTuning = (): RenderTuning => DEFAULT_RENDER_TUNING;
const defaultGetSubmarinePower = (submarine: SubmarineState): number =>
  submarine.batteryCharge ?? 0;
const defaultGetGraphicsQuality = (): GraphicsQuality => DEFAULT_GRAPHICS_QUALITY;

export const createHarpoonRoomController = (
  deps: HarpoonRoomControllerDeps
): HarpoonRoomController => {
  const {
    controls,
    emitControl = noopEmitControl,
    getRenderTuning = defaultGetRenderTuning,
    getSubmarinePower = defaultGetSubmarinePower,
    getGraphicsQuality = defaultGetGraphicsQuality,
    getFpsOverride
  } = deps;

  let harpoonCameraView: HarpoonCameraView | null = null;
  let harpoonFired = false;
  let harpoonReloading = false;
  let harpoonReloadStartMs = 0;
  let harpoonReloadFrameId: number | null = null;

  let harpoonActionButton: HTMLButtonElement | null = null;
  let harpoonScanButton: HTMLButtonElement | null = null;
  let harpoonReloadProgressBar: HTMLDivElement | null = null;
  let harpoonReloadStatusLabel: HTMLDivElement | null = null;

  let harpoonScanFlashOverlay: HTMLDivElement | null = null;
  let harpoonScanStatusOverlay: HTMLDivElement | null = null;
  let harpoonScanStatusTimerId: number | null = null;
  let harpoonScanFlashTimerId: number | null = null;

  const harpoonAimState = { yaw: 0, pitch: 0 };

  const setHarpoonReloadProgress = (progress: number): void => {
    if (harpoonReloadProgressBar) {
      harpoonReloadProgressBar.style.width = `${Math.round(progress * 100)}%`;
    }
  };

  const updateHarpoonButtonStates = (): void => {
    const reloading = harpoonReloading;
    if (harpoonActionButton) {
      if (reloading) {
        harpoonActionButton.disabled = true;
        harpoonActionButton.textContent = t('mining_laser_cooling');
      } else if (harpoonFired) {
        harpoonActionButton.disabled = false;
        harpoonActionButton.textContent = t('mining_laser_stop');
      } else {
        harpoonActionButton.disabled = false;
        harpoonActionButton.textContent = t('mining_laser_fire');
      }
    }
    if (harpoonScanButton) {
      harpoonScanButton.disabled = !controllerState.lastSnapshot
        || controllerState.lastSnapshot.submarine.engineOverloaded
        || getSubmarinePower(controllerState.lastSnapshot.submarine) < HARPOON_SCAN_POWER_MW;
    }
    if (harpoonReloadStatusLabel) {
      if (harpoonReloading) {
        const remainingMs = Math.max(0, HARPOON_RELOAD_DURATION_MS - (performance.now() - harpoonReloadStartMs));
        const remainingSeconds = Math.ceil(remainingMs / 1000);
        harpoonReloadStatusLabel.textContent = `Cooling down: ${remainingSeconds}s`;
      } else if (harpoonFired) {
        harpoonReloadStatusLabel.textContent = t('mining_laser_armed');
      } else {
        harpoonReloadStatusLabel.textContent = t('mining_laser_ready');
      }
    }
  };

  const advanceHarpoonReload = (timestampMs: number): void => {
    if (!harpoonReloading) {
      return;
    }

    const elapsed = timestampMs - harpoonReloadStartMs;
    const progress = Math.min(1, elapsed / HARPOON_RELOAD_DURATION_MS);
    setHarpoonReloadProgress(progress);

    if (elapsed >= HARPOON_RELOAD_DURATION_MS) {
      harpoonReloading = false;
      updateHarpoonButtonStates();
      harpoonReloadFrameId = null;
      return;
    }

    harpoonReloadFrameId = window.requestAnimationFrame(advanceHarpoonReload);
  };

  const startHarpoonReload = (): void => {
    if (harpoonReloading) {
      return;
    }

    harpoonReloading = true;
    harpoonReloadStartMs = performance.now();
    setHarpoonReloadProgress(0);
    updateHarpoonButtonStates();
    harpoonReloadFrameId = window.requestAnimationFrame(advanceHarpoonReload);
  };

  const stopHarpoonReload = (): void => {
    if (harpoonReloadFrameId !== null) {
      window.cancelAnimationFrame(harpoonReloadFrameId);
      harpoonReloadFrameId = null;
    }
    harpoonReloading = false;
    setHarpoonReloadProgress(1);
    updateHarpoonButtonStates();
  };

  const playHarpoonScanVisualFeedback = (): void => {
    if (harpoonScanFlashOverlay) {
      harpoonScanFlashOverlay.style.opacity = '1';
      if (harpoonScanFlashTimerId !== null) {
        window.clearTimeout(harpoonScanFlashTimerId);
      }
      harpoonScanFlashTimerId = window.setTimeout(() => {
        if (harpoonScanFlashOverlay) {
          harpoonScanFlashOverlay.style.opacity = '0';
        }
        harpoonScanFlashTimerId = null;
      }, 170);
    }

    if (harpoonScanStatusOverlay) {
      harpoonScanStatusOverlay.style.opacity = '1';
      if (harpoonScanStatusTimerId !== null) {
        window.clearTimeout(harpoonScanStatusTimerId);
      }
      harpoonScanStatusTimerId = window.setTimeout(() => {
        if (harpoonScanStatusOverlay) {
          harpoonScanStatusOverlay.style.opacity = '0';
        }
        harpoonScanStatusTimerId = null;
      }, 1200);
    }
  };

  const bindHarpoonScanVisualElements = (
    flashOverlay: HTMLDivElement | null,
    statusOverlay: HTMLDivElement | null
  ): void => {
    if (harpoonScanStatusTimerId !== null) {
      window.clearTimeout(harpoonScanStatusTimerId);
      harpoonScanStatusTimerId = null;
    }
    if (harpoonScanFlashTimerId !== null) {
      window.clearTimeout(harpoonScanFlashTimerId);
      harpoonScanFlashTimerId = null;
    }

    if (harpoonScanFlashOverlay) {
      harpoonScanFlashOverlay.style.opacity = '0';
    }
    if (harpoonScanStatusOverlay) {
      harpoonScanStatusOverlay.style.opacity = '0';
    }

    harpoonScanFlashOverlay = flashOverlay;
    harpoonScanStatusOverlay = statusOverlay;
  };

  const tryAction = (): boolean => {
    if (harpoonReloading || !controllerState.currentRoomCode || !controllerState.lastSnapshot) {
      return false;
    }

    if (!harpoonFired) {
      const fired = harpoonCameraView?.fireHarpoon() ?? false;
      if (fired) {
        emitControl({ action: 'mining_laser_fire' });
        harpoonFired = true;
      }
    } else {
      harpoonCameraView?.retrieveHarpoon();
      emitControl({ action: 'mining_laser_stop' });
      harpoonFired = false;
      startHarpoonReload();
    }

    updateHarpoonButtonStates();
    return true;
  };

  const tryScan = (): boolean => {
    if (!controllerState.currentRoomCode || !controllerState.lastSnapshot) {
      return false;
    }

    playHarpoonScanVisualFeedback();
    harpoonCameraView?.triggerScan(controllerState.lastSnapshot);
    emitControl({ action: 'mining_scan' });
    updateHarpoonButtonStates();
    return true;
  };

  const bindUiElements = (
    actionButton: HTMLButtonElement | null,
    scanButton: HTMLButtonElement | null,
    reloadProgressBar: HTMLDivElement | null,
    reloadStatusLabel: HTMLDivElement | null
  ): void => {
    harpoonActionButton = actionButton;
    harpoonScanButton = scanButton;
    harpoonReloadProgressBar = reloadProgressBar;
    harpoonReloadStatusLabel = reloadStatusLabel;
    updateHarpoonButtonStates();
  };

  const clearUiElements = (): void => {
    bindUiElements(null, null, null, null);
  };

  const setRenderTuning = (tuning: RenderTuning): void => {
    harpoonCameraView?.setRenderTuning(tuning);
  };

  const setSnapshot = (snapshot: GameSnapshot): void => {
    harpoonCameraView?.setSnapshot(snapshot);
  };

  const setRoomState = (state: RoomState): void => {
    harpoonCameraView?.setRoomState(state);
  };

  const dispose = (): void => {
    stopHarpoonReload();
    harpoonCameraView?.dispose();
    harpoonCameraView = null;
    clearUiElements();
  };

  const render = (): void => {
    if (!controls) {
      return;
    }

    controls.classList.add('camera-role-layout');
    const cameraCard = createCard('Mining laser camera', 'View the submarine hull from the bottom-mounted mining laser camera.');
    cameraCard.classList.add('ctrl-room-main-card');
    const cameraViewport = document.createElement('div');
    cameraViewport.className = 'harpoon-camera-viewport role-camera-viewport';

    const reticle = document.createElement('div');
    reticle.style.position = 'absolute';
    reticle.style.left = '50%';
    reticle.style.top = '50%';
    reticle.style.width = '30px';
    reticle.style.height = '30px';
    reticle.style.pointerEvents = 'none';
    reticle.style.transform = 'translate(-50%, -50%)';
    reticle.style.boxSizing = 'border-box';
    reticle.style.border = '1px solid rgba(122, 217, 255, 0.92)';
    reticle.style.borderRadius = '50%';
    reticle.style.boxShadow = '0 0 10px rgba(122, 217, 255, 0.45)';
    reticle.style.zIndex = '1';
    reticle.innerHTML = '<div style="position:absolute;left:50%;top:0;width:2px;height:100%;background:rgba(122,217,255,0.9);transform:translateX(-50%);"></div><div style="position:absolute;top:50%;left:0;width:100%;height:2px;background:rgba(122,217,255,0.9);transform:translateY(-50%);"></div>';
    cameraViewport.append(reticle);

    const scanFlashOverlay = document.createElement('div');
    scanFlashOverlay.style.position = 'absolute';
    scanFlashOverlay.style.inset = '0';
    scanFlashOverlay.style.pointerEvents = 'none';
    scanFlashOverlay.style.background = 'rgba(255,255,255,0.95)';
    scanFlashOverlay.style.opacity = '0';
    scanFlashOverlay.style.transition = 'opacity 0.16s ease-out';
    scanFlashOverlay.style.zIndex = '2147483647';
    scanFlashOverlay.style.mixBlendMode = 'screen';
    cameraViewport.append(scanFlashOverlay);

    const scanStatusOverlay = document.createElement('div');
    scanStatusOverlay.style.position = 'absolute';
    scanStatusOverlay.style.top = '1rem';
    scanStatusOverlay.style.left = '50%';
    scanStatusOverlay.style.transform = 'translateX(-50%)';
    scanStatusOverlay.style.padding = '0.5rem 1rem';
    scanStatusOverlay.style.background = 'rgba(0, 0, 0, 0.72)';
    scanStatusOverlay.style.color = '#ffffff';
    scanStatusOverlay.style.fontWeight = '700';
    scanStatusOverlay.style.letterSpacing = '0.08em';
    scanStatusOverlay.style.borderRadius = '999px';
    scanStatusOverlay.style.opacity = '0';
    scanStatusOverlay.style.pointerEvents = 'none';
    scanStatusOverlay.style.transition = 'opacity 0.2s ease-out';
    scanStatusOverlay.style.zIndex = '2147483648';
    scanStatusOverlay.style.fontSize = '0.9rem';
    scanStatusOverlay.textContent = t('scanning');
    cameraViewport.append(scanStatusOverlay);

    bindHarpoonScanVisualElements(scanFlashOverlay, scanStatusOverlay);

    cameraCard.append(cameraViewport);
    controls.append(cameraCard);

    harpoonCameraView?.dispose();
    harpoonCameraView = new HarpoonCameraView(
      cameraViewport,
      getGraphicsQuality(),
      getRenderTuning(),
      getFpsOverride?.()
    );
    harpoonCameraView.setRockRetrievedCallback((payload) => {
      emitControl({
        action: 'harpoon_rock_retrieved',
        value: payload.resourceValue,
        resourceType: payload.resourceType
      });
    });
    if (controllerState.lastRoomState) {
      harpoonCameraView.setRoomState(controllerState.lastRoomState);
    }
    if (controllerState.lastSnapshot) {
      harpoonCameraView.setSnapshot(controllerState.lastSnapshot);
    }
    harpoonCameraView.setYawPitch(harpoonAimState.yaw, harpoonAimState.pitch);
    harpoonCameraView.setAutoRetractCallback(() => {
      if (!harpoonFired) {
        return;
      }

      harpoonFired = false;
      startHarpoonReload();
      updateHarpoonButtonStates();
    });
    if (harpoonFired && !harpoonReloading) {
      harpoonCameraView.fireHarpoon();
    }

    updateHarpoonButtonStates();

    const updateAim = (yaw: number, pitch: number): void => {
      harpoonAimState.yaw = yaw;
      harpoonAimState.pitch = clamp(pitch, -89, 89);
      harpoonCameraView?.setYawPitch(harpoonAimState.yaw, harpoonAimState.pitch);
    };

    let aimDragging = false;
    let dragPointerX = 0;
    let dragPointerY = 0;

    cameraViewport.style.touchAction = 'none';
    cameraViewport.addEventListener('pointerdown', (event) => {
      aimDragging = true;
      dragPointerX = event.clientX;
      dragPointerY = event.clientY;
      cameraViewport.setPointerCapture(event.pointerId);
    });

    cameraViewport.addEventListener('pointermove', (event) => {
      if (!aimDragging) {
        return;
      }

      const rect = cameraViewport.getBoundingClientRect();
      const yawDelta = -((event.clientX - dragPointerX) / Math.max(1, rect.width)) * 90;
      const pitchDelta = -((event.clientY - dragPointerY) / Math.max(1, rect.height)) * 90;
      dragPointerX = event.clientX;
      dragPointerY = event.clientY;

      updateAim(harpoonAimState.yaw + yawDelta, harpoonAimState.pitch + pitchDelta);
    });

    const stopAimDrag = (): void => {
      aimDragging = false;
    };

    cameraViewport.addEventListener('pointerup', stopAimDrag);
    cameraViewport.addEventListener('pointercancel', stopAimDrag);
  };

  return {
    render,
    tryAction,
    tryScan,
    bindUiElements,
    clearUiElements,
    setRenderTuning,
    setSnapshot,
    setRoomState,
    dispose
  };
};
